import {
  Users,
  FileText,
  Calendar,
  TrendingUp,
  Plus,
  Activity,
} from "lucide-react";
import { AdminLayout } from "@jambushrushti/components/admin/AdminLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Badge } from "@jambushrushti/components/ui/badge";
import { Progress } from "@jambushrushti/components/ui/progress";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@jambushrushti/integrations/supabase/client";

const recentActivities: Array<{
  action: string;
  user: string;
  time: string;
}> = [];

export default function AdminDashboard() {
  // Count-only queries: the dashboard needs totals, not the rows themselves.
  const { data: profilesRes } = useQuery({
    queryKey: ["admin-profiles-count"],
    queryFn: () =>
      supabase.from("profiles").select("id", { count: "exact", head: true }),
  });

  const { data: manuscriptsRes } = useQuery({
    queryKey: ["admin-manuscripts-count"],
    queryFn: () =>
      supabase.from("manuscripts").select("id", { count: "exact", head: true }),
  });

  const { data: eventsRes } = useQuery({
    queryKey: ["admin-events-count"],
    queryFn: () =>
      supabase.from("events").select("id", { count: "exact", head: true }),
  });

  const totalUsers = profilesRes?.count ?? 0;
  const totalManuscripts = manuscriptsRes?.count ?? 0;
  const upcomingEvents = eventsRes?.count ?? 0;

  const statsCards = [
    {
      title: "Total Users",
      value: totalUsers.toLocaleString(),
      icon: Users,
      color: "bg-primary/10 text-primary",
    },
    {
      title: "Manuscripts",
      value: totalManuscripts.toString(),
      icon: FileText,
      color: "bg-burgundy/10 text-burgundy",
    },
    {
      title: "Upcoming Events",
      value: upcomingEvents.toString(),
      icon: Calendar,
      color: "bg-accent/10 text-accent",
    },
  ];

  return (
    <AdminLayout title="Dashboard">
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-umber-900">
              Dashboard Overview
            </h1>
            <p className="mt-1 text-sm text-umber-500">
              Welcome back! Here is your real-time ecosystem stats.
            </p>
          </div>
          <Button className="w-fit rounded-xl bg-gradient-to-r from-heritage-600 to-heritage-500 font-semibold text-white shadow-md shadow-heritage-500/20 hover:from-heritage-700 hover:to-heritage-600 md:w-auto">
            <Plus className="mr-2 h-4 w-4" />
            Quick Action
          </Button>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {statsCards.map(stat => {
            const Icon = stat.icon;
            return (
              <Card
                key={stat.title}
                className="shadow-xs rounded-2xl border border-umber-200/80 bg-card transition-all hover:shadow-md"
              >
                <CardContent className="p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <div
                      className={`flex h-10 w-10 items-center justify-center rounded-xl ${stat.color}`}
                    >
                      <Icon className="h-5 w-5" />
                    </div>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-umber-900">
                      {stat.value}
                    </p>
                    <p className="mt-1 text-xs font-medium text-umber-500">
                      {stat.title}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom Section */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Recent Activities */}
          <Card className="shadow-xs rounded-2xl border border-umber-200/80 bg-card">
            <CardHeader className="border-b border-umber-100 pb-3">
              <CardTitle className="font-display text-base font-bold text-umber-900">
                Recent Activities
              </CardTitle>
              <CardDescription className="text-xs text-umber-500">
                Latest actions across the ecosystem
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-3">
                {recentActivities.length === 0 && (
                  <p className="py-10 text-center text-sm text-umber-500">
                    No recent activity available.
                  </p>
                )}
                {recentActivities.map((activity, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3.5 rounded-xl border border-transparent p-2.5 transition-colors hover:border-heritage-100 hover:bg-heritage-50/40"
                  >
                    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-heritage-100/60 text-heritage-800">
                      <Activity className="h-4 w-4 text-heritage-700" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-umber-900">
                        {activity.action}
                      </p>
                      <p className="text-xs text-umber-500">{activity.user}</p>
                    </div>
                    <span className="whitespace-nowrap rounded-full bg-umber-100 px-2 py-0.5 text-xs font-medium text-umber-400">
                      {activity.time}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* System Status */}
          <Card className="shadow-xs rounded-2xl border border-umber-200/80 bg-card">
            <CardHeader className="border-b border-umber-100 pb-3">
              <CardTitle className="font-display text-base font-bold text-umber-900">
                System Status
              </CardTitle>
              <CardDescription className="text-xs text-umber-500">
                Index engine and storage indicators
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5 pt-4">
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-xs font-semibold text-umber-700">
                    PDF Search Index
                  </span>
                  <span className="text-xs text-umber-400">Not connected</span>
                </div>
                <Progress
                  value={0}
                  className="h-2 rounded-full bg-umber-100 [&>div]:bg-heritage-500"
                />
              </div>
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-xs font-semibold text-umber-700">
                    Storage Used
                  </span>
                  <span className="text-xs text-umber-400">Not connected</span>
                </div>
                <Progress
                  value={0}
                  className="h-2 rounded-full bg-umber-100 [&>div]:bg-emerald-500"
                />
              </div>
              <div>
                <div className="mb-1.5 flex items-center justify-between">
                  <span className="text-xs font-semibold text-umber-700">
                    Research Entries
                  </span>
                  <span className="text-xs text-umber-400">Not connected</span>
                </div>
                <Progress
                  value={0}
                  className="h-2 rounded-full bg-umber-100 [&>div]:bg-heritage-600"
                />
              </div>
              <div className="flex items-center justify-between border-t border-umber-100 pt-2">
                <div>
                  <span className="text-xs font-semibold text-umber-700">
                    AI Processing Pipeline
                  </span>
                  <p className="text-[11px] text-umber-400">
                    No telemetry source configured
                  </p>
                </div>
                <Badge className="border border-umber-200 bg-umber-100 text-xs font-semibold text-umber-500">
                  Unavailable
                </Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}
